import React, { useState, useEffect } from 'react';
import './AdminCustomers.css';

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, verified, unverified

  useEffect(() => {
    const loadCustomers = () => {
      // Get saved customers from localStorage (real data only, no mock)
      const savedCustomers = localStorage.getItem('adminCustomers');
      
      if (savedCustomers) {
        try {
          const customers = JSON.parse(savedCustomers);
          setCustomers(customers);
        } catch (e) {
          console.error('Error parsing saved customers:', e);
          setCustomers([]);
        }
      } else {
        // No customers yet, start with empty array
        setCustomers([]);
      }
    };

    loadCustomers();
    
    // Poll for updates
    const interval = setInterval(loadCustomers, 3000);
    return () => clearInterval(interval);
  }, []);

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = 
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.id.toString().includes(searchTerm);
    
    const matchesFilter = 
      filter === 'all' ||
      (filter === 'verified' && customer.emailVerified && customer.phoneVerified) ||
      (filter === 'unverified' && (!customer.emailVerified || !customer.phoneVerified));
    
    return matchesSearch && matchesFilter;
  });

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="admin-customers">
      <div className="admin-customers-header">
        <div className="admin-customers-stats">
          <div className="admin-stat-card">
            <div className="admin-stat-value">{customers.length}</div>
            <div className="admin-stat-label">Total Customers</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">
              {customers.filter(c => c.emailVerified && c.phoneVerified).length}
            </div>
            <div className="admin-stat-label">Verified</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">
              {formatCurrency(customers.reduce((sum, c) => sum + c.balance, 0))}
            </div>
            <div className="admin-stat-label">Total Balance</div>
          </div>
        </div>
        
        <div className="admin-customers-controls">
          <div className="admin-customers-filters">
            <button
              className={`admin-filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`admin-filter-btn ${filter === 'verified' ? 'active' : ''}`}
              onClick={() => setFilter('verified')}
            >
              Verified
            </button>
            <button
              className={`admin-filter-btn ${filter === 'unverified' ? 'active' : ''}`}
              onClick={() => setFilter('unverified')}
            >
              Unverified
            </button>
          </div>
          <div className="admin-customers-search">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-search-input"
            />
          </div>
        </div>
      </div>

      <div className="admin-customers-table-container">
        <table className="admin-customers-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Verification</th>
              <th>Balance</th>
              <th>Total Deposits</th>
              <th>Total Withdrawals</th>
              <th>Registration Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan="9" className="admin-table-empty">
                  No customers found
                </td>
              </tr>
            ) : (
              filteredCustomers.map((customer) => (
                <tr key={customer.id}>
                  <td className="admin-table-id">#{customer.id}</td>
                  <td>
                    <div className="admin-table-user-name">{customer.name}</div>
                  </td>
                  <td>
                    <div className="admin-table-user-email">{customer.email}</div>
                  </td>
                  <td>
                    <span className={`admin-status-badge status-${customer.status}`}>
                      {customer.status}
                    </span>
                  </td>
                  <td>
                    <div className="admin-verification-status">
                      <span className={`verification-badge ${customer.emailVerified ? 'verified' : 'unverified'}`}>
                        {customer.emailVerified ? '✓ Email' : '✗ Email'}
                      </span>
                      <span className={`verification-badge ${customer.phoneVerified ? 'verified' : 'unverified'}`}>
                        {customer.phoneVerified ? '✓ Phone' : '✗ Phone'}
                      </span>
                    </div>
                  </td>
                  <td className="admin-table-amount">{formatCurrency(customer.balance)}</td>
                  <td className="admin-table-amount">{formatCurrency(customer.totalDeposits)}</td>
                  <td className="admin-table-amount">{formatCurrency(customer.totalWithdrawals)}</td>
                  <td className="admin-table-date">{formatDate(customer.registrationDate)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCustomers;

